import { supabase } from "../../supabase";
import {
  applyOrganizationScope,
  orgScopedInsert,
  orgScopedUpdateByPk,
  resolveOrganizationIdForQueries,
} from "../orgQueryScope";
import {
  resolvePayrollTypeDisplayLabel,
  TIME_LOG_PAYROLL_TYPE_OPTIONS,
} from "../payrollHelpers";
import {
  DEFAULT_PAYROLL_TYPES,
  RESERVED_PAYROLL_TYPE_KEYS,
  SYSTEM_PAYROLL_TYPE_KEYS,
} from "./catalogDefaults";
import {
  loadCatalogCache,
  persistCatalogCache,
  sortCatalogItems,
  toDropdownOptions,
  validateCatalogKey,
  validateCatalogName,
} from "./catalogHelpers";

const TABLE = "payroll_types";
const ID_COLUMN = "payroll_type_id";
const CACHE_KEY = "CP_PAYROLL_TYPES_CACHE_V1";

function normalizePayrollTypeRow(row) {
  return {
    payroll_type_id: row.payroll_type_id,
    organization_id: row.organization_id ?? null,
    type_key: row.type_key,
    display_label: row.display_label,
    display_order: Number(row.display_order ?? 0),
    is_active: row.is_active !== false,
    is_default: row.is_default === true,
    is_system: row.is_system === true,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

export function getFallbackPayrollTypes() {
  return DEFAULT_PAYROLL_TYPES.map((entry, index) => ({
    payroll_type_id: `fallback-${index}`,
    organization_id: null,
    type_key: entry.type_key,
    display_label: entry.display_label,
    display_order: entry.display_order,
    is_active: true,
    is_default: entry.type_key === "Standard",
    is_system: entry.is_system === true,
  }));
}

export function isReservedPayrollTypeKey(typeKey) {
  return RESERVED_PAYROLL_TYPE_KEYS.has(typeKey);
}

export function isSystemPayrollType(typeKey) {
  return SYSTEM_PAYROLL_TYPE_KEYS.has(typeKey);
}

export function payrollTypesToDropdownOptions(
  items,
  { includeEmpty = false } = {},
) {
  const labeled = sortCatalogItems(items).map((item) => ({
    ...item,
    display_label: resolvePayrollTypeDisplayLabel(
      item.type_key,
      item.display_label,
    ),
  }));

  return toDropdownOptions(labeled, {
    valueKey: "type_key",
    labelKey: "display_label",
    includeEmpty,
    emptyLabel: "Select Payroll Type",
  });
}

/** Map org catalog rows → Start Session / editor options (system keys as values). */
export function buildPayrollTypeSessionOptions(items) {
  if (!items?.length) {
    return [...TIME_LOG_PAYROLL_TYPE_OPTIONS];
  }

  return sortCatalogItems(items).map((item) => ({
    value: item.type_key,
    label: resolvePayrollTypeDisplayLabel(item.type_key, item.display_label),
  }));
}

/**
 * Org payroll type options for Start Session / Manual Entry.
 * Offline/error → hardcoded TIME_LOG_PAYROLL_TYPE_OPTIONS.
 */
export async function loadPayrollTypeSessionOptions(
  client = supabase,
  actor = null,
) {
  try {
    const rows = await loadPayrollTypesForConsumers(client, actor);
    if (rows.length > 0) {
      return buildPayrollTypeSessionOptions(rows);
    }
  } catch (err) {
    console.warn(
      "[payrollTypeCatalog] session options load failed — using hardcoded fallback",
      err,
    );
  }

  return [...TIME_LOG_PAYROLL_TYPE_OPTIONS];
}

export async function listPayrollTypes(client = supabase, options = {}) {
  const { activeOnly = false, organizationId = null, actor = null } = options;

  const resolvedOrgId =
    organizationId ??
    (await resolveOrganizationIdForQueries(actor, client));

  let query = client
    .from(TABLE)
    .select(
      "payroll_type_id, organization_id, type_key, display_label, display_order, is_active, is_default, is_system, created_at, updated_at",
    )
    .order("display_order", { ascending: true })
    .order("type_key", { ascending: true });

  query = applyOrganizationScope(query, resolvedOrgId, { tableName: TABLE });

  if (activeOnly) {
    query = query.eq("is_active", true);
  }

  const { data, error } = await query;
  if (error) {
    throw error;
  }

  return (data || []).map(normalizePayrollTypeRow);
}

/**
 * Active payroll types for consumers.
 * Successful empty org catalog → []. Cache/fallback only on network/error.
 */
export async function loadPayrollTypesForConsumers(client = supabase, actor = null) {
  const organizationId = await resolveOrganizationIdForQueries(actor, client);

  try {
    const rows = await listPayrollTypes(client, { activeOnly: true, actor });
    persistCatalogCache(CACHE_KEY, rows, organizationId);
    return rows;
  } catch (err) {
    console.warn("[payrollTypeCatalog] load failed — using cache/fallback", err);
  }

  const cached = loadCatalogCache(CACHE_KEY, organizationId);
  if (cached?.length) {
    return cached.map(normalizePayrollTypeRow);
  }

  return getFallbackPayrollTypes();
}

export async function getPayrollTypeDropdownOptions(
  client = supabase,
  actor = null,
) {
  const items = await loadPayrollTypesForConsumers(client, actor);
  return payrollTypesToDropdownOptions(items);
}

export async function createPayrollType(
  { type_key, display_label, display_order, is_default = false },
  client = supabase,
  actor = null,
) {
  const keyError = validateCatalogKey(type_key, "Type key");
  if (keyError) {
    throw new Error(keyError);
  }

  const labelError = validateCatalogName(display_label, "Display label");
  if (labelError) {
    throw new Error(labelError);
  }

  const trimmedKey = type_key.trim();

  const { data, error } = await orgScopedInsert(
    client,
    TABLE,
    [
      {
        type_key: trimmedKey,
        display_label: display_label.trim(),
        display_order: Number(display_order ?? 0),
        is_default,
        is_active: true,
        is_system: isSystemPayrollType(trimmedKey),
      },
    ],
    actor,
    {
      select:
        "payroll_type_id, organization_id, type_key, display_label, display_order, is_active, is_default, is_system, created_at, updated_at",
      single: true,
    },
  );

  if (error) {
    throw error;
  }

  return normalizePayrollTypeRow(data);
}

export async function updatePayrollType(
  payrollTypeId,
  patch,
  client = supabase,
  actor = null,
) {
  if (!actor) {
    throw new Error("Signed-in administrator required.");
  }

  let existingQuery = client
    .from(TABLE)
    .select("is_system, type_key, organization_id")
    .eq(ID_COLUMN, payrollTypeId);

  const organizationId = await resolveOrganizationIdForQueries(actor, client);
  existingQuery = applyOrganizationScope(existingQuery, organizationId, {
    tableName: TABLE,
  });

  const existing = await existingQuery.maybeSingle();

  if (existing.error) {
    throw existing.error;
  }

  if (!existing.data) {
    throw new Error("Payroll type not found in your organization.");
  }

  const existingKey = existing.data.type_key;
  const isSystemRow =
    existing.data.is_system === true || isSystemPayrollType(existingKey);

  if (isSystemRow && patch.is_active === false) {
    throw new Error("System payroll types cannot be deactivated.");
  }

  if (patch.type_key != null) {
    const nextKey = patch.type_key.trim();
    if (
      (isReservedPayrollTypeKey(existingKey) || isSystemRow) &&
      nextKey !== existingKey
    ) {
      throw new Error("Reserved payroll type keys cannot be changed.");
    }
  }

  if (patch.display_label != null) {
    const labelError = validateCatalogName(patch.display_label, "Display label");
    if (labelError) {
      throw new Error(labelError);
    }
  }

  const payload = {
    ...(patch.type_key != null && !isReservedPayrollTypeKey(existingKey)
      ? { type_key: patch.type_key.trim() }
      : {}),
    ...(patch.display_label != null
      ? { display_label: patch.display_label.trim() }
      : {}),
    ...(patch.display_order != null
      ? { display_order: Number(patch.display_order) }
      : {}),
    ...(patch.is_active != null ? { is_active: patch.is_active } : {}),
    ...(patch.is_default != null ? { is_default: patch.is_default } : {}),
  };

  const builder = await orgScopedUpdateByPk(client, TABLE, payload, actor, {
    pkColumn: ID_COLUMN,
    pkValue: payrollTypeId,
  });

  if (builder?.error) {
    throw builder.error;
  }

  const { data, error } = await builder
    .select(
      "payroll_type_id, organization_id, type_key, display_label, display_order, is_active, is_default, is_system, created_at, updated_at",
    )
    .maybeSingle();

  if (error) {
    throw error;
  }

  if (!data) {
    throw new Error("Payroll type not found in your organization.");
  }

  return normalizePayrollTypeRow(data);
}

export async function setDefaultPayrollType(
  payrollTypeId,
  client = supabase,
  actor = null,
) {
  const organizationId = await resolveOrganizationIdForQueries(actor, client);

  if (!organizationId) {
    throw new Error("organization_id is required for payroll type update.");
  }

  let clearQuery = client
    .from(TABLE)
    .update({ is_default: false })
    .neq(ID_COLUMN, payrollTypeId);

  clearQuery = applyOrganizationScope(clearQuery, organizationId, {
    tableName: TABLE,
  });

  const { error: clearError } = await clearQuery;
  if (clearError) {
    throw clearError;
  }

  return updatePayrollType(
    payrollTypeId,
    { is_default: true },
    client,
    actor,
  );
}

export async function deactivatePayrollType(
  payrollTypeId,
  client = supabase,
  actor = null,
) {
  return updatePayrollType(
    payrollTypeId,
    { is_active: false },
    client,
    actor,
  );
}

export async function activatePayrollType(
  payrollTypeId,
  client = supabase,
  actor = null,
) {
  return updatePayrollType(payrollTypeId, { is_active: true }, client, actor);
}
